/* ============================================================
   ProductTour — the first-time guided tour (Next / Back / Skip).

   Shows once per user and tour (tours.js picks which): after the user finishes or
   skips it, POST /users/me/tours/{id} records it and it never shows again. Not while
   an admin views as a role or user (that view is read-only and is not them).

   Each step points at the real thing on screen — a sidebar page, a top-bar button —
   with a spotlight and a card beside it. When the thing is not on screen (a phone,
   where pages live in the menu) the card shows on its own; on a phone it is a bottom
   sheet. Keyboard: → / Enter next, ← back, Esc skips. Focus stays in the card.

   It only reads the page (selectors) and adds an overlay: nothing else on the page
   changes, and a crash in here hides the tour, never the app.
   ============================================================ */

import { Component, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { useAuth } from '../auth/AuthContext'
import { BrandGlyph } from '../components/BrandMark'
import Icon from '../ui/icons'
import { qk } from '../lib/queryKeys'
import * as usersApi from '../api/users'
import { TOUR_REPLAY_EVENT, roleTour, tourFor } from './tours'
import '../styles/tour.css'

const PHONE = '(max-width: 640px)'
const START_DELAY_MS = 900 // let the page settle before the tour starts
const GAP = 16 // space between the spotlight and the card
const EDGE = 12 // keep the card this far from the screen edge
const PAD = 6 // spotlight padding around its target

const isPhone = () => Boolean(window.matchMedia?.(PHONE).matches)
const sessionKey = (userId, tourId) => `tf-tour-done:${userId}:${tourId}`
function doneThisSession(key) {
  try { return sessionStorage.getItem(key) === '1' } catch { return false }
}
function rememberThisSession(key) {
  try { sessionStorage.setItem(key, '1') } catch { /* storage off: the server still remembers */ }
}

/** The step's target when it is on screen right now, else null. `selector` may list
    several (the desktop search box, the phone search button): the first one showing wins. */
function findTarget(selector) {
  if (!selector) return null
  let els = []
  try { els = [...document.querySelectorAll(selector)] } catch { return null }
  for (const el of els) {
    // A page lower in a long sidebar: bring it into view, but only when the sidebar itself
    // is on screen (on a tablet it is a closed drawer).
    const sidebar = el.closest('.sidebar')
    if (sidebar && sidebar.getBoundingClientRect().right > 0) el.scrollIntoView({ block: 'nearest' })
    const style = getComputedStyle(el)
    if (style.display === 'none' || style.visibility === 'hidden') continue
    const r = el.getBoundingClientRect()
    if (r.width < 4 || r.height < 4) continue
    if (r.right <= 0 || r.bottom <= 0 || r.left >= window.innerWidth || r.top >= window.innerHeight) continue
    return el
  }
  return null
}

const clamp = (v, lo, hi) => Math.max(lo, Math.min(v, hi))

/** Where the card goes next to the spotlight: the first side it fits on. */
function placeCard(spot, cw, ch) {
  const vw = window.innerWidth, vh = window.innerHeight
  const cx = spot.left + spot.width / 2, cy = spot.top + spot.height / 2
  const order = cx < vw * 0.35 ? ['right', 'bottom', 'top', 'left']
    : spot.top < vh * 0.3 ? ['bottom', 'left', 'right', 'top']
      : ['top', 'left', 'right', 'bottom']
  for (const side of order) {
    let top, left
    if (side === 'right' || side === 'left') {
      left = side === 'right' ? spot.left + spot.width + GAP : spot.left - GAP - cw
      top = clamp(cy - ch / 2, EDGE, vh - ch - EDGE)
      if (left < EDGE || left + cw > vw - EDGE) continue
      return { side, top, left, arrow: { top: clamp(cy - top, 22, ch - 22) } }
    }
    top = side === 'bottom' ? spot.top + spot.height + GAP : spot.top - GAP - ch
    left = clamp(cx - cw / 2, EDGE, vw - cw - EDGE)
    if (top < EDGE || top + ch > vh - EDGE) continue
    return { side, top, left, arrow: { left: clamp(cx - left, 22, cw - 22) } }
  }
  return null // no room beside it: the card goes to the middle
}

/** A crash in the tour hides the tour; the rest of the app keeps working. */
class TourBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { failed: false }
  }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error) {
    console.warn('Guided tour stopped:', error?.message)
  }

  render() {
    return this.state.failed ? null : this.props.children
  }
}

export default function ProductTour() {
  return (
    <TourBoundary>
      <TourGate />
    </TourBoundary>
  )
}

/** Decides whether a tour runs: fresh /users/me data, not a preview, a tour for this role
    the user has not seen, not already closed in this tab. */
function TourGate() {
  const { can, status } = useAuth()
  const qc = useQueryClient()
  // The same cache entry AuthProvider fills; this observer never fetches on its own. Only
  // the server's answer counts: the copy saved in the session can be older.
  const me = useQuery({ queryKey: qk.auth.me(), queryFn: () => usersApi.me().then((r) => r.data), enabled: false })
  const fresh = me.data
  const tour = fresh && !fresh.preview && status === 'authenticated' ? tourFor(fresh) : null
  const [run, setRun] = useState(null)

  useEffect(() => {
    if (run || !tour || !fresh?.id) return undefined
    if (doneThisSession(sessionKey(fresh.id, tour.id))) return undefined
    const timer = setTimeout(() => {
      const steps = tour.build({ user: fresh, can }).filter((s) => !s.optional || findTarget(s.target))
      if (steps.length) setRun({ tour, userId: fresh.id, steps })
    }, START_DELAY_MS)
    return () => clearTimeout(timer)
    // Start once per user and tour; later /users/me refreshes must not restart it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, tour?.id, fresh?.id])

  // "Take the tour" (profile menu / phone menu): the same tour again, now, seen or not.
  // A short wait lets the menu that was clicked close first.
  useEffect(() => {
    const onReplay = () => {
      if (!fresh?.id || fresh.preview) return
      const t = roleTour(fresh)
      if (!t) return
      setTimeout(() => {
        const steps = t.build({ user: fresh, can }).filter((s) => !s.optional || findTarget(s.target))
        if (steps.length) setRun((current) => current || { tour: t, userId: fresh.id, steps })
      }, 350)
    }
    window.addEventListener(TOUR_REPLAY_EVENT, onReplay)
    return () => window.removeEventListener(TOUR_REPLAY_EVENT, onReplay)
  }, [fresh, can])

  const close = useCallback(() => {
    if (!run) return
    const { tour: t, userId } = run
    rememberThisSession(sessionKey(userId, t.id))
    setRun(null)
    usersApi.markTourSeen(t.id)
      .then((res) => {
        const seen = res?.data?.tours_seen
        if (seen) qc.setQueryData(qk.auth.me(), (old) => (old && old.id === userId ? { ...old, tours_seen: seen } : old))
      })
      .catch(() => { /* not saved: it shows again next sign-in, never twice in this tab */ })
  }, [run, qc])

  if (!run) return null
  return createPortal(<TourOverlay steps={run.steps} onClose={close} />, document.body)
}

function TourOverlay({ steps, onClose }) {
  const [index, setIndex] = useState(0)
  const [geo, setGeo] = useState({ mode: 'center', spot: null, card: null })
  const cardRef = useRef(null)
  const nextRef = useRef(null)
  const ids = useId()
  const step = steps[index]
  const total = steps.length
  const last = index === total - 1

  const next = useCallback(() => (last ? onClose() : setIndex((i) => Math.min(i + 1, total - 1))), [last, onClose, total])
  const back = useCallback(() => setIndex((i) => Math.max(i - 1, 0)), [])

  // Where everything goes for this step; again on resize / scroll.
  const measure = useCallback(() => {
    const card = cardRef.current
    if (!card) return
    const phone = isPhone()
    const el = step.hero ? null : findTarget(step.target)
    let spot = null
    if (el) {
      const r = el.getBoundingClientRect()
      const round = getComputedStyle(el).borderRadius === '50%'
      spot = { top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2, radius: round ? 999 : 14 }
    }
    if (phone && !step.hero) {
      // Bottom sheet; at the top when the spotlight would sit under it (the AI button).
      const top = spot && spot.top + spot.height > window.innerHeight - card.offsetHeight - 24
      setGeo({ mode: top ? 'sheet-top' : 'sheet', spot, card: null })
      return
    }
    const placed = spot ? placeCard(spot, card.offsetWidth, card.offsetHeight) : null
    setGeo({ mode: placed ? 'anchored' : 'center', spot, card: placed })
  }, [step])

  useLayoutEffect(() => {
    measure()
    // The sidebar may still be scrolling the page into view: settle once more.
    const t = setTimeout(measure, 220)
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      clearTimeout(t)
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [measure])

  // Focus: the main button of each step; back to where it was when the tour ends.
  useEffect(() => {
    const before = document.activeElement
    return () => { if (before && typeof before.focus === 'function') before.focus({ preventScroll: true }) }
  }, [])
  useEffect(() => { nextRef.current?.focus({ preventScroll: true }) }, [index])

  // Keys: → / ← move, Esc skips, Tab stays inside the card. Captured first so the page's
  // own shortcuts (Esc closes menus, ⌘K search) do not fire under the tour.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); return }
      if (e.key === 'ArrowRight') { e.preventDefault(); e.stopPropagation(); next(); return }
      if (e.key === 'ArrowLeft') { e.preventDefault(); e.stopPropagation(); back(); return }
      if (e.key === 'Tab' && cardRef.current) {
        const items = [...cardRef.current.querySelectorAll('button:not([disabled])')]
        if (!items.length) return
        const first = items[0], lastItem = items[items.length - 1]
        const inside = cardRef.current.contains(document.activeElement)
        if (e.shiftKey && (document.activeElement === first || !inside)) { e.preventDefault(); lastItem.focus() }
        else if (!e.shiftKey && (document.activeElement === lastItem || !inside)) { e.preventDefault(); first.focus() }
        return
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); e.stopPropagation() }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [next, back, onClose])

  const { mode, spot, card } = geo
  const cardStyle = mode === 'anchored' && card ? { top: card.top, left: card.left } : undefined
  const progress = Math.round(((index + 1) / total) * 100)
  const nextLabel = index === 0 && step.hero ? 'Show me around' : last ? 'Let’s go' : 'Next'

  return (
    <div className="tour-root">
      {spot
        ? <div className="tour-spot" style={{ top: spot.top, left: spot.left, width: spot.width, height: spot.height, borderRadius: spot.radius }} aria-hidden="true" />
        : <div className="tour-scrim" aria-hidden="true" />}

      <div
        ref={cardRef}
        className={`tour-card is-${mode}${step.hero ? ' is-hero' : ''}${step.finale ? ' is-finale' : ''}`}
        style={{ ...cardStyle, '--k': step.tone }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${ids}-title`}
        aria-describedby={`${ids}-body`}
      >
        {mode === 'anchored' && card && (
          <span className={`tour-arrow on-${card.side}`} style={card.arrow} aria-hidden="true" />
        )}
        <div className="tour-card-in" key={index}>
          {step.hero ? (
            <div className="tour-hero" aria-hidden="true">
              <span className="tour-hero-dot d1" /><span className="tour-hero-dot d2" /><span className="tour-hero-dot d3" />
              <span className="tour-hero-dot d4" /><span className="tour-hero-dot d5" />
              <span className="tour-hero-badge">{step.finale ? <Icon name="check-circle" /> : <BrandGlyph />}</span>
            </div>
          ) : (
            <div className="tour-head">
              <span className="tour-icon" aria-hidden="true"><Icon name={step.icon} /></span>
              <span className="tour-eyebrow">{step.eyebrow}</span>
              <button type="button" className="tour-x" onClick={onClose} aria-label="Skip tour"><Icon name="x" /></button>
            </div>
          )}

          <h2 className="tour-title" id={`${ids}-title`}>{step.title}</h2>
          <p className="tour-body" id={`${ids}-body`}>{step.body}</p>
          {step.points?.length > 0 && (
            <ul className="tour-points">
              {step.points.map((p) => <li key={p}>{p}</li>)}
            </ul>
          )}
          {step.chips?.length > 0 && (
            <div className="tour-chips">
              <span className="tour-chips-label">You can</span>
              {step.chips.map((c) => <span key={c} className="tour-chip">{c}</span>)}
            </div>
          )}
        </div>

        <div className="tour-progress" aria-hidden="true"><span style={{ width: `${progress}%` }} /></div>
        <div className="tour-foot">
          {!last ? (
            <button type="button" className="tour-skip" onClick={onClose}>Skip tour</button>
          ) : <span />}
          <span className="tour-count" aria-hidden="true">{index + 1} / {total}</span>
          <div className="tour-buttons">
            {index > 0 && !last && (
              <button type="button" className="btn btn-secondary btn-sm tour-back" onClick={back} aria-label="Back">
                <Icon name="chevron-left" />
              </button>
            )}
            <button type="button" ref={nextRef} className="btn btn-primary btn-sm tour-next" onClick={next}>
              {nextLabel} {!last && <Icon name="arrow-right" />}
            </button>
          </div>
        </div>
        <p className="tour-sr" aria-live="polite">Step {index + 1} of {total}: {step.title}</p>
      </div>
    </div>
  )
}
